import { DEFAULT_CURRENCY } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/dealOptions/dealOptionsFormatters';
import {
  buildDealOptionsViewModel,
  getNightCount,
} from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/dealOptions/dealOptionsModel';
import type {
  DealOptionGroup,
  DealOptionRow,
} from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/dealOptions/dealOptionsModel';
import { parseOccupancyText } from '~/components/Offers/Detail/OfferBuilder/shared/occupancyUtils';

import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';
import type { OpportunityRelatedDeal, OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { uniqueBy } from '~/utils/arrayUtils';
import { formatAudCurrency, formatNumber } from '~/utils/numberFormatters';

import type { InclusionApplies, InclusionApplyTo, InclusionDraft } from './types';

export function formatInclusionPrv(value: number | undefined) {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '—';
  }
  return formatNumber(value, 2, { pad: false });
}

// See docs/business-rules.md#prv-value-display.
const DEFAULT_LEAD_GUESTS = 2;

export function getLeadNights(groups: Array<DealOptionGroup>) {
  return getAvailableNights(groups)[0];
}

// See docs/business-rules.md#salesforce-data-model.
export function getUniqueInclusionAssets(inclusions: Array<OpportunityRelatedInclusion> = []) {
  const assets = inclusions
    .map((inclusion) => inclusion.asset)
    .filter((asset): asset is OpportunityRoomAsset => !!asset);

  return uniqueBy(assets, (asset) => asset.sfid);
}

// See docs/business-rules.md#salesforce-data-model.
export function deriveOpportunityCurrency(deals: Array<OpportunityRelatedDeal> = []) {
  return deals.find((deal) => deal.currencyisocode)?.currencyisocode ?? DEFAULT_CURRENCY;
}

// See docs/business-rules.md#salesforce-data-model.
export function getDealOccupancy(deal: OpportunityRelatedDeal) {
  const parsed = parseOccupancyText(deal.name);
  if (!parsed) {
    return undefined;
  }

  return `${parsed.adults}A${parsed.extraCount ? `${parsed.extraCount}${parsed.extraType}` : ''}`;
}

export function getAvailableRoomTypes(groups: Array<DealOptionGroup>) {
  return Array.from(new Set(groups.map((group) => group.roomName))).sort((roomNameA, roomNameB) =>
    roomNameA.localeCompare(roomNameB),
  );
}

export function getAvailableOccupancies(groups: Array<DealOptionGroup>) {
  const occupancies = new Set<string>();
  groups.forEach((group) =>
    group.deals.forEach((dealRow) => {
      const occupancy = getDealOccupancy(dealRow.deal);
      if (occupancy) {
        occupancies.add(occupancy);
      }
    }),
  );
  return Array.from(occupancies).sort((occupancyA, occupancyB) => occupancyA.localeCompare(occupancyB));
}

export function getAvailableNights(groups: Array<DealOptionGroup>) {
  const nights = new Set<number>();
  groups.forEach((group) =>
    group.deals.forEach((dealRow) => {
      const nightCount = getNightCount(dealRow.deal);
      if (nightCount) {
        nights.add(nightCount);
      }
    }),
  );
  return Array.from(nights).sort((nightsA, nightsB) => nightsA - nightsB);
}

export function defaultApplyTo(): InclusionApplyTo {
  return { roomTypes: 'all', occupancies: 'all', los: 'all' };
}

// See docs/business-rules.md#applies-to-filtering.
export function dealMatchesApplyTo(dealRow: DealOptionRow, roomName: string, applyTo: InclusionApplyTo) {
  if (applyTo.roomTypes !== 'all' && !applyTo.roomTypes.includes(roomName)) {
    return false;
  }

  if (applyTo.occupancies !== 'all') {
    const occupancy = getDealOccupancy(dealRow.deal);
    if (!occupancy || !applyTo.occupancies.includes(occupancy)) {
      return false;
    }
  }

  if (applyTo.los !== 'all') {
    const nightCount = getNightCount(dealRow.deal);
    if (nightCount === undefined || !applyTo.los.includes(nightCount)) {
      return false;
    }
  }

  return true;
}

export function describeApplyTo(applyTo: InclusionApplyTo) {
  if (applyTo.roomTypes === 'all' && applyTo.occupancies === 'all' && applyTo.los === 'all') {
    return 'All packages';
  }

  const parts: Array<string> = [];
  if (applyTo.roomTypes !== 'all') {
    parts.push(applyTo.roomTypes.length === 1 ? applyTo.roomTypes[0] : `${applyTo.roomTypes.length} room types`);
  }
  if (applyTo.occupancies !== 'all') {
    parts.push(applyTo.occupancies.length === 1 ? applyTo.occupancies[0] : `${applyTo.occupancies.length} occupancies`);
  }
  if (applyTo.los !== 'all') {
    parts.push(applyTo.los.length === 1 ? `${applyTo.los[0]}N` : `${applyTo.los.length} LOS`);
  }
  return parts.join(' · ') || 'No packages selected';
}

// See docs/business-rules.md#prv-value-display.
export function describeInclusionValue({
  prv,
  type,
  applies,
  leadNights,
  leadGuests = DEFAULT_LEAD_GUESTS,
}: {
  prv: number | undefined;
  type?: string;
  applies?: InclusionApplies;
  leadNights?: number;
  leadGuests?: number;
}) {
  if (!prv) {
    return 'No value set - contributes nothing to PRV';
  }

  const bits = [`${formatAudCurrency(prv)} total value`];
  let per = prv;
  const units: Array<string> = [];

  if (type === 'Daily' && leadNights && leadNights > 1) {
    per /= leadNights;
    units.push('night');
  }
  if (applies === 'Per Person' && leadGuests > 1) {
    per /= leadGuests;
    units.push('guest');
  }

  if (units.length && leadNights) {
    const roundedPer = per < 10 ? per.toFixed(2) : per.toFixed(0);
    bits.push(`≈ $${roundedPer} per ${units.join(', per ')} across ${leadNights}N for ${leadGuests}`);
  }

  return bits.join(' · ');
}

export function sumInclusionValue(drafts: Array<InclusionDraft>) {
  return drafts.reduce((sum, draft) => sum + (draft.prv ?? 0), 0);
}

export interface DealOptionRowWithDrafts extends DealOptionRow {
  draftInclusions: Array<InclusionDraft>;
  nightCount?: number;
}

export interface DealOptionInclusionGroup extends Omit<DealOptionGroup, 'deals'> {
  deals: Array<DealOptionRowWithDrafts>;
}

// Reuses the Deal Options tab's own room/deal-option grouping, then folds in local drafts
// (matched via applyTo) alongside already-synced inclusions.
export function buildDealOptionInclusionGroups({
  deals,
  inclusions,
  drafts,
}: {
  deals: Array<OpportunityRelatedDeal>;
  inclusions: Array<OpportunityRelatedInclusion>;
  drafts: Array<InclusionDraft>;
}): Array<DealOptionInclusionGroup> {
  const { groups } = buildDealOptionsViewModel({ deals, inclusions });

  return groups.map((group) => ({
    ...group,
    deals: group.deals.map((dealRow) => ({
      ...dealRow,
      nightCount: getNightCount(dealRow.deal),
      draftInclusions: drafts.filter((draft) => dealMatchesApplyTo(dealRow, group.roomName, draft.applyTo)),
    })),
  }));
}
